import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tab,
  Tabs,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore,
  Code,
  Security,
  Speed,
  CheckCircle,
  Warning,
  Error,
  ContentCopy,
  Key,
  Api,
  Shield,
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import toast from 'react-hot-toast';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`api-tabpanel-${index}`}
      aria-labelledby={`api-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ApiDocs: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Code copied to clipboard!');
  };

  const endpoints = [
    {
      method: 'POST',
      path: '/api/prompt/process',
      description: 'Process a prompt through the LLM with bidirectional security filtering (inbound + outbound)',
      permissions: ['prompt.process'],
      rateLimit: 'API Key specific',
      parameters: [
        { name: 'prompt', type: 'string', required: true, description: 'The prompt to process' },
        { name: 'model', type: 'string', required: false, description: 'LLM model to use (default: gpt-3.5-turbo)' },
        { name: 'temperature', type: 'number', required: false, description: 'Model temperature (0-1)' },
        { name: 'max_tokens', type: 'number', required: false, description: 'Maximum tokens in response' },
        { name: 'context', type: 'object', required: false, description: 'Additional context with outbound filtering options' },
      ],
    },
    {
      method: 'POST',
      path: '/api/prompt/check',
      description: 'Check prompt security without processing through LLM',
      permissions: ['prompt.check'],
      rateLimit: 'API Key specific',
      parameters: [
        { name: 'prompt', type: 'string', required: true, description: 'The prompt to check' },
      ],
    },
    {
      method: 'POST',
      path: '/api/prompts/analyze',
      description: 'Lightweight security analysis of a prompt',
      permissions: ['prompt.check'],
      rateLimit: 'API Key specific',
      parameters: [
        { name: 'prompt', type: 'string', required: true, description: 'The prompt to analyze' },
      ],
    },
    {
      method: 'GET',
      path: '/api/prompt/history',
      description: 'Get user\'s prompt history',
      permissions: ['prompt.history'],
      rateLimit: 'API Key specific',
      parameters: [
        { name: 'page', type: 'number', required: false, description: 'Page number (default: 1)' },
        { name: 'limit', type: 'number', required: false, description: 'Items per page (default: 20)' },
        { name: 'status', type: 'string', required: false, description: 'Filter by status (allowed, blocked, flagged, modified)' },
      ],
    },
    {
      method: 'POST',
      path: '/api/security/analyze-response',
      description: 'Analyze LLM responses for outbound security violations',
      permissions: ['prompt.check'],
      rateLimit: 'API Key specific',
      parameters: [
        { name: 'response', type: 'string', required: true, description: 'The response text to analyze' },
        { name: 'context', type: 'object', required: false, description: 'Analysis context with filtering options' },
      ],
    },
    {
      method: 'GET',
      path: '/api/admin/security-rules',
      description: 'Get security rules with enhanced outbound filtering support',
      permissions: ['rules.manage'],
      rateLimit: 'API Key specific',
      parameters: [
        { name: 'direction', type: 'string', required: false, description: 'Filter by direction (inbound, outbound, both)' },
        { name: 'category', type: 'string', required: false, description: 'Filter by category (includes outbound categories)' },
        { name: 'action', type: 'string', required: false, description: 'Filter by action (block, flag, redact, modify, alert)' },
      ],
    },
    {
      method: 'POST',
      path: '/api/admin/security-rules',
      description: 'Create new security rule with outbound filtering capabilities',
      permissions: ['rules.manage'],
      rateLimit: 'API Key specific',
      parameters: [
        { name: 'name', type: 'string', required: true, description: 'Rule name' },
        { name: 'direction', type: 'string', required: false, description: 'Rule direction (inbound, outbound, both)' },
        { name: 'category', type: 'string', required: false, description: 'Security category (includes outbound categories)' },
        { name: 'action', type: 'string', required: true, description: 'Action to take (block, flag, redact, modify, alert)' },
        { name: 'response_action', type: 'object', required: false, description: 'Outbound-specific response configuration' },
      ],
    },
  ];

  const codeExamples = {
    javascript: `// Using fetch API
const response = await fetch('https://app.neurastrike.ai/api/prompt/process', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'ask_your_api_key_here'
  },
  body: JSON.stringify({
    prompt: 'What is artificial intelligence?',
    model: 'gpt-3.5-turbo',
    temperature: 0.7
  })
});

const data = await response.json();
console.log(data);`,

    python: `import requests

url = "https://app.neurastrike.ai/api/prompt/process"
headers = {
    "Content-Type": "application/json",
    "X-API-Key": "ask_your_api_key_here"
}
data = {
    "prompt": "What is artificial intelligence?",
    "model": "gpt-3.5-turbo",
    "temperature": 0.7
}

response = requests.post(url, json=data, headers=headers)
result = response.json()
print(result)`,

    curl: `curl -X POST "https://app.neurastrike.ai/api/prompt/process" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ask_your_api_key_here" \\
  -d '{
    "prompt": "What is artificial intelligence?",
    "model": "gpt-3.5-turbo",
    "temperature": 0.7
  }'`,

    nodejs: `const axios = require('axios');

const config = {
  method: 'post',
  url: 'https://app.neurastrike.ai/api/prompt/process',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'ask_your_api_key_here'
  },
  data: {
    prompt: 'What is artificial intelligence?',
    model: 'gpt-3.5-turbo',
    temperature: 0.7
  }
};

try {
  const response = await axios(config);
  console.log(response.data);
} catch (error) {
  console.error(error.response.data);
}`
  };

  const responseExamples = {
    success: `{
  "id": 12345,
  "response": "Artificial intelligence (AI) refers to the simulation of human intelligence in machines...",
  "status": "allowed",
  "threat_score": 0.1,
  "detected_threats": [
    {
      "type": "safe_content",
      "confidence": 0.95,
      "category": "educational",
      "direction": "inbound"
    }
  ],
  "metadata": {
    "tokens_used": 150,
    "model": "gpt-3.5-turbo",
    "processing_time_ms": 1250,
    "was_filtered": false,
    "inbound_filtered": false,
    "outbound_filtered": false
  }
}`,

    blocked: `{
  "error": "Response blocked due to security policy violations",
  "code": "OUTBOUND_SECURITY_VIOLATION",
  "details": {
    "threat_score": 0.9,
    "violations": [
      {
        "rule_name": "Credit Card Detection",
        "category": "data_leakage",
        "action": "block",
        "direction": "outbound"
      }
    ],
    "analysis_id": "resp_456",
    "timestamp": "2024-01-15T10:30:00Z"
  }
}`,

    modified: `{
  "id": 12347,
  "response": "I cannot provide customer email addresses due to privacy policies.",
  "original_response": "The customer's email is [EMAIL REDACTED]",
  "status": "modified",
  "threat_score": 0.6,
  "detected_threats": [
    {
      "type": "data_leakage",
      "confidence": 0.95,
      "category": "email_address",
      "direction": "outbound"
    }
  ],
  "outbound_analysis": {
    "violations_detected": 1,
    "actions_taken": ["redact"],
    "filtered_content": ["email_address"],
    "security_headers": {
      "X-Security-Filtered": "true",
      "X-Security-Actions": "redact"
    }
  },
  "metadata": {
    "tokens_used": 156,
    "model": "gpt-3.5-turbo",
    "processing_time_ms": 1247,
    "was_filtered": true,
    "inbound_filtered": false,
    "outbound_filtered": true
  }
}`,

    error: `{
  "error": "Rate limit exceeded",
  "code": "RATE_LIMIT_EXCEEDED",
  "rate_limit": 100,
  "window_seconds": 3600,
  "current_usage": 100,
  "reset_time": 1703123456
}`
  };

  const statusCodes = [
    { code: 200, status: 'OK', description: 'Request successful' },
    { code: 400, status: 'Bad Request', description: 'Invalid request parameters' },
    { code: 401, status: 'Unauthorized', description: 'Invalid or missing API key' },
    { code: 403, status: 'Forbidden', description: 'Insufficient permissions' },
    { code: 429, status: 'Too Many Requests', description: 'Rate limit exceeded' },
    { code: 500, status: 'Internal Server Error', description: 'Server error' },
  ];

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Api color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4" component="h1">
          API Documentation
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1">
          Use your API keys to integrate GuardLLM into your applications. 
          All requests require authentication via API key in the <code>X-API-Key</code> header.
        </Typography>
      </Alert>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="API documentation tabs">
          <Tab label="Quick Start" />
          <Tab label="Endpoints" />
          <Tab label="Code Examples" />
          <Tab label="Response Format" />
          <Tab label="Error Handling" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h5" gutterBottom>
            Quick Start Guide
          </Typography>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                1. Get Your API Key
              </Typography>
              <Typography variant="body1" paragraph>
                Navigate to the <strong>API Keys</strong> section and create a new API key with the required permissions.
              </Typography>

              <Typography variant="h6" gutterBottom>
                2. Base URL
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.100', mb: 2 }}>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  https://app.neurastrike.ai/api
                </Typography>
              </Paper>

              <Typography variant="h6" gutterBottom>
                3. Authentication
              </Typography>
              <Typography variant="body1" paragraph>
                Include your API key in the <code>X-API-Key</code> header:
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.100', mb: 2 }}>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  X-API-Key: ask_your_api_key_here
                </Typography>
              </Paper>

              <Typography variant="h6" gutterBottom>
                4. Bidirectional Security Filtering
              </Typography>
              <Typography variant="body1" paragraph>
                GuardLLM provides comprehensive protection with both inbound and outbound filtering:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><Security /></ListItemIcon>
                  <ListItemText 
                    primary="Inbound Filtering" 
                    secondary="Protects against prompt injection, jailbreaks, and malicious inputs" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Shield /></ListItemIcon>
                  <ListItemText 
                    primary="Outbound Filtering" 
                    secondary="Prevents data leakage, credential exposure, and sensitive information disclosure" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle /></ListItemIcon>
                  <ListItemText 
                    primary="Real-time Analysis" 
                    secondary="Sub-100ms filtering with minimal impact on response time" 
                  />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                5. Rate Limiting
              </Typography>
              <Typography variant="body1">
                Each API key has its own rate limit. Check the response headers for current usage:
              </Typography>
              <List>
                <ListItem>
                  <ListItemText 
                    primary="X-RateLimit-Limit" 
                    secondary="Total requests allowed per window" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="X-RateLimit-Remaining" 
                    secondary="Requests remaining in current window" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="X-RateLimit-Reset" 
                    secondary="Unix timestamp when limit resets" 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h5" gutterBottom>
            API Endpoints
          </Typography>

          {endpoints.map((endpoint, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip 
                    label={endpoint.method} 
                    color={endpoint.method === 'GET' ? 'success' : 'primary'} 
                    size="small"
                  />
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {endpoint.path}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph>
                  {endpoint.description}
                </Typography>

                <Typography variant="h6" gutterBottom>
                  Required Permissions
                </Typography>
                <Box display="flex" gap={1} mb={2}>
                  {endpoint.permissions.map((permission) => (
                    <Chip key={permission} label={permission} size="small" />
                  ))}
                </Box>

                <Typography variant="h6" gutterBottom>
                  Parameters
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Parameter</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Required</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {endpoint.parameters.map((param, paramIndex) => (
                        <TableRow key={paramIndex}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{param.name}</TableCell>
                          <TableCell>{param.type}</TableCell>
                          <TableCell>
                            {param.required ? (
                              <Chip label="Required" color="error" size="small" />
                            ) : (
                              <Chip label="Optional" color="default" size="small" />
                            )}
                          </TableCell>
                          <TableCell>{param.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h5" gutterBottom>
            Code Examples
          </Typography>

          {Object.entries(codeExamples).map(([language, code]) => (
            <Accordion key={language} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Code />
                  <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                    {language === 'nodejs' ? 'Node.js' : language}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box position="relative">
                  <Tooltip title="Copy code">
                    <IconButton
                      sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                      onClick={() => copyToClipboard(code)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                  <SyntaxHighlighter
                    language={language === 'nodejs' ? 'javascript' : language}
                    style={tomorrow}
                    customStyle={{ borderRadius: 8 }}
                  >
                    {code}
                  </SyntaxHighlighter>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h5" gutterBottom>
            Response Format
          </Typography>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <CheckCircle color="success" />
                <Typography variant="h6">Successful Response</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <SyntaxHighlighter language="json" style={tomorrow}>
                {responseExamples.success}
              </SyntaxHighlighter>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Security color="info" />
                <Typography variant="h6">Modified Response (Outbound Filtered)</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Response with sensitive content filtered by outbound security rules
              </Typography>
              <SyntaxHighlighter language="json" style={tomorrow}>
                {responseExamples.modified}
              </SyntaxHighlighter>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Warning color="warning" />
                <Typography variant="h6">Blocked Response</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Response blocked due to outbound security violations
              </Typography>
              <SyntaxHighlighter language="json" style={tomorrow}>
                {responseExamples.blocked}
              </SyntaxHighlighter>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Error color="error" />
                <Typography variant="h6">Error Response</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <SyntaxHighlighter language="json" style={tomorrow}>
                {responseExamples.error}
              </SyntaxHighlighter>
            </AccordionDetails>
          </Accordion>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Security Headers</strong>
            </Typography>
            <Typography variant="body2">
              When outbound filtering is applied, responses include additional security headers:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="X-Security-Filtered" 
                  secondary="Indicates if response was filtered (true/false)" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="X-Security-Actions" 
                  secondary="Actions taken (e.g., 'redact,flag')" 
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="X-Threat-Score" 
                  secondary="Overall threat score (0-1)" 
                />
              </ListItem>
            </List>
          </Alert>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Typography variant="h5" gutterBottom>
            Error Handling
          </Typography>

          <Typography variant="h6" gutterBottom>
            HTTP Status Codes
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statusCodes.map((status) => (
                  <TableRow key={status.code}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{status.code}</TableCell>
                    <TableCell>{status.status}</TableCell>
                    <TableCell>{status.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" gutterBottom>
            Error Response Structure
          </Typography>
          <Typography variant="body1" paragraph>
            All error responses follow a consistent structure:
          </Typography>
          <SyntaxHighlighter language="json" style={tomorrow}>
            {`{
  "error": "Human readable error message",
  "code": "MACHINE_READABLE_ERROR_CODE",
  "details": {
    // Additional error-specific details
  }
}`}
          </SyntaxHighlighter>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Common Error Codes
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="INVALID_API_KEY" 
                secondary="The provided API key is invalid or expired" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="PERMISSION_DENIED" 
                secondary="API key lacks required permission for this endpoint" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="RATE_LIMIT_EXCEEDED" 
                secondary="API key has exceeded its rate limit" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="VALIDATION_ERROR" 
                secondary="Request parameters failed validation" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="PROMPT_BLOCKED" 
                secondary="Prompt was blocked due to security concerns" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="OUTBOUND_SECURITY_VIOLATION" 
                secondary="Response blocked or modified due to outbound filtering rules" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="OUTBOUND_ANALYSIS_FAILED" 
                secondary="Outbound response analysis service error" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="FILTER_TIMEOUT" 
                secondary="Response filtering timed out" 
              />
            </ListItem>
          </List>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default ApiDocs; 